import React, { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  Filter,
  Flag,
  Search,
  Shield,
  AlertCircle,
  Check,
  X,
  ExternalLink,
  MoreVertical,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Upload,
  ChevronRight
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

const TransactionMonitoring = () => {
      useBreadcrumbs([
            { label: 'Escrow Management', href: '#' },
            { label: 'Escrow Transactions Monitoring' }
          ]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [selectedView, setSelectedView] = useState('summary');
  const [selectedReport, setSelectedReport] = useState('daily');

  // Mock data for demonstration
  const stats = {
    totalTransactions: 1245,
    activeTransactions: 456,
    disputedTransactions: 23,
    highRiskTransactions: 15,
    totalValue: 2500000,
    pendingDocuments: 45,
    pendingInspections: 12,
    successRate: 98.5,
    avgProcessingTime: '2.3 days'
  };

  // Mock data for transaction trends
  const trendData = [
    { time: '00:00', transactions: 25, value: 150000 },
    { time: '04:00', transactions: 40, value: 280000 },
    { time: '08:00', transactions: 65, value: 420000 },
    { time: '12:00', transactions: 85, value: 580000 },
    { time: '16:00', transactions: 95, value: 750000 },
    { time: '20:00', transactions: 110, value: 890000 },
  ];

  // Mock data for transactions
  const transactions = [
    {
      id: 'TX123456',
      type: 'Property',
      amount: 250000,
      status: 'in_progress',
      buyer: 'John Doe',
      seller: 'Jane Smith',
      createdAt: '2024-02-13T10:30:00',
      documentStatus: 'pending_review',
      inspectionDate: '2024-02-15T14:00:00',
      riskLevel: 'low',
      paymentStatus: 'funds_held',
      disputes: [],
    },
    {
      id: 'TX123457',
      type: 'Vehicle',
      amount: 45000,
      status: 'disputed',
      buyer: 'Mike Johnson',
      seller: 'Car Dealership Inc',
      createdAt: '2024-02-12T15:45:00',
      documentStatus: 'rejected',
      inspectionDate: '2024-02-14T11:00:00',
      riskLevel: 'high',
      paymentStatus: 'pending',
      disputes: ['vehicle_condition'],
    },
  ];

  // Status distribution data
  const statusDistribution = [
    { name: 'Completed', value: 350, color: '#10B981' },
    { name: 'In Progress', value: 275, color: '#3B82F6' },
    { name: 'Pending', value: 180, color: '#F59E0B' },
    { name: 'Disputed', value: 45, color: '#EF4444' }
  ];

  // Document verification stats
  const documentStats = {
    pending: 45,
    verified: 380,
    rejected: 25,
    expired: 15
  };

  const getStatusBadge = (status: 'completed' | 'in_progress' | 'disputed' | 'pending') => {
      const statusStyles: Record<'completed' | 'in_progress' | 'disputed' | 'pending', string> = {
        completed: 'bg-green-100 text-green-800',
        in_progress: 'bg-blue-100 text-blue-800',
        disputed: 'bg-red-100 text-red-800',
        pending: 'bg-yellow-100 text-yellow-800'
      };
      return `px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[status]}`;
  };

  const getRiskBadge = (level: 'low' | 'medium' | 'high') => {
    const riskStyles = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return `px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${riskStyles[level]}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Active Transactions */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Transactions
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.activeTransactions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Value */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Value
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        ${(stats.totalValue / 1000000).toFixed(2)}M
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Check className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Success Rate
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.successRate}%
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Processing Time */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Avg Processing Time
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.avgProcessingTime}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Real-time Activity */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Real-time Transaction Activity</h3>
              <select
                className="mt-1 block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="transactions" stroke="#8884d8" name="Transactions" />
                  <Line yAxisId="right" type="monotone" dataKey="value" stroke="#82ca9d" name="Value ($)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="max-w-xs relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3 sm:mt-0 sm:ml-4">
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="disputed">Disputed</option>
              <option value="high_risk">High Risk</option>
            </select>
          </div>
        </div>

        {/* Transaction Table */}
        <div className="mt-8 flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documents
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Risk Level
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {transaction.id}
                              </div>
                              <div className="text-sm text-gray-500">
                                {transaction.type} - ${transaction.amount.toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getStatusBadge(transaction.status as 'completed' | 'in_progress' | 'disputed' | 'pending')}>
                            {transaction.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                          <FileText className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {transaction.documentStatus.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {transaction.paymentStatus.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getRiskBadge(transaction.riskLevel as 'low' | 'medium' | 'high')}>
                            {transaction.riskLevel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900">
                            <MoreVertical className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Document Verification & Status Distribution */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Document Verification Status */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Document Verification Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-yellow-400" />
                    <span className="ml-2 text-sm text-gray-500">Pending Review</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{documentStats.pending}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-yellow-400 rounded-full" 
                        style={{ width: `${(documentStats.pending / (documentStats.pending + documentStats.verified + documentStats.rejected + documentStats.expired)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Check className="h-5 w-5 text-green-400" />
                    <span className="ml-2 text-sm text-gray-500">Verified</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{documentStats.verified}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-green-400 rounded-full" 
                        style={{ width: `${(documentStats.verified / (documentStats.pending + documentStats.verified + documentStats.rejected + documentStats.expired)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <X className="h-5 w-5 text-red-400" />
                    <span className="ml-2 text-sm text-gray-500">Rejected</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{documentStats.rejected}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-red-400 rounded-full" 
                        style={{ width: `${(documentStats.rejected / (documentStats.pending + documentStats.verified + documentStats.rejected + documentStats.expired)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Expired</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{documentStats.expired}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-gray-400 rounded-full" 
                        style={{ width: `${(documentStats.expired / (documentStats.pending + documentStats.verified + documentStats.rejected + documentStats.expired)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction Status Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Active Disputes and High Risk Transactions */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Active Disputes */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Active Disputes</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {transactions
                .filter(t => t.disputes && t.disputes.length > 0)
                .map((transaction) => (
                <div key={transaction.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {transaction.id} - {transaction.type}
                        </p>
                        <p className="text-sm text-red-500">
                          {transaction.disputes.join(', ').replace(/_/g, ' ')}
                        </p>
                      </div>
                    </div>
                    <button className="ml-6 bg-white px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                      Resolve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* High Risk Transactions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">High Risk Transactions</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {transactions
                .filter(t => t.riskLevel === 'high')
                .map((transaction) => (
                <div key={transaction.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Flag className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {transaction.id} - ${transaction.amount.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500">
                          {transaction.buyer} → {transaction.seller}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-4">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </span>
                      <button className="bg-red-100 px-3 py-1 border border-red-200 rounded-md text-sm font-medium text-red-700 hover:bg-red-50">
                        Review
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Inspection Schedule */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Upcoming Inspections</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {transactions
                .filter(t => t.inspectionDate && new Date(t.inspectionDate) > new Date())
                .map((transaction) => (
                <div key={transaction.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {transaction.type} Inspection - {transaction.id}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(transaction.inspectionDate).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button className="ml-6 bg-white px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TransactionMonitoring;